import { Appointment, AppointmentTypes } from "../../../../models/entities/appointments.model";
import { HelperService } from "../../../../services/helpers/helper.service";

/** Minutes a card survives past its start when the type's duration is unknown. */
export const UNKNOWN_DURATION_GRACE_MINUTES = 30;

export interface TodaysAppointmentCard {
	id: string;
	startsAt: Date;
	/** null when the type is not in the /appointment-types response. */
	durationInMinutes: number | null;
	type: string;
	participant: {
		/**
		 * The `/participant/:participantId` route id, already resolved.
		 *
		 * NOT the raw `participant.id` from the API — `/coach-appointments` does not
		 * return one (see `HelperService.getParticipantRouteId`), so this is derived
		 * from the Kannact ID. null only when the row carries neither.
		 */
		routeId: string | null;
		kannactId: string | null;
		name: string;
		phone: string | null;
	};
}

/**
 * Local calendar date as YYYY-MM-DD — the coach's own day, not UTC.
 * Necessary because a west-coast coach at 11 PM will see the next UTC day
 * in toISOString(), which would load appointments from the wrong date.
 */
export const toLocalDateParam = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/**
 * The call length only. Padding is deliberately ignored (Maria: "duration of
 * the call, no padding considered"). Drops unknown types by returning null
 * so the caller can apply a fallback grace period instead of guessing.
 */
export const getAppointmentDurationMinutes = (
	type: string,
	types: AppointmentTypes[] | undefined
): number | null => {
	if (!types) return null;
	const found = types.find((t) => t.type === type);
	return found ? found.durationInMinutes : null;
};

/**
 * Epoch ms at which a card stops being shown. For unknown durations,
 * applies a bounded grace period so the card doesn't vanish at pickup
 * (before the coach can react) or stay all day (making the rail lie).
 */
export const getAppointmentEndTime = (startsAt: Date, durationInMinutes: number | null): number =>
	startsAt.getTime() + (durationInMinutes ?? UNKNOWN_DURATION_GRACE_MINUTES) * 60_000;

/**
 * true while the appointment has not yet ended. Boundary is strict:
 * a 9:00 appointment of 20 minutes is visible at 9:19 and gone at 9:20 exactly.
 */
export const isAppointmentVisible = (
	startsAt: Date,
	durationInMinutes: number | null,
	now: Date
): boolean => {
	const endTime = getAppointmentEndTime(startsAt, durationInMinutes);
	return now.getTime() < endTime;
};

/**
 * The cards the rail shows right now, oldest first.
 *
 * Every exclusion here is deliberate and each one is a tested boundary:
 * cancelled rows (the only status the system actually stores — there is no
 * "confirmed"), rows whose `datetime` does not parse, rows that are not on the
 * coach's LOCAL calendar day (the endpoint takes a date window, so what counts
 * as "today" is decided here, in the client), and rows whose end time has
 * already passed. Pure: the clock is injected, never read.
 */
export const selectTodaysAppointments = (
	appointments: Appointment[] | undefined,
	types: AppointmentTypes[] | undefined,
	now: Date
): TodaysAppointmentCard[] => {
	if (!appointments) return [];

	const today = toLocalDateParam(now);

	return (
		appointments
			.filter((appointment) => !appointment.cancelled)
			.map((appointment) => ({
				appointment,
				startsAt: new Date(appointment.datetime),
				durationInMinutes: getAppointmentDurationMinutes(appointment.type, types),
			}))
			// An unparseable datetime is dropped rather than rendered as "Invalid Date".
			.filter(({ startsAt }) => !Number.isNaN(startsAt.getTime()))
			.filter(({ startsAt }) => toLocalDateParam(startsAt) === today)
			.filter(({ startsAt, durationInMinutes }) =>
				isAppointmentVisible(startsAt, durationInMinutes, now)
			)
			.map(({ appointment, startsAt, durationInMinutes }) => {
				const participant = appointment.participant;
				const phone = participant?.phone?.trim();
				return {
					id: appointment.id,
					startsAt,
					durationInMinutes,
					type: appointment.type as string,
					participant: {
						routeId: HelperService.getParticipantRouteId(
							participant?.id,
							participant?.kannactId
						),
						kannactId: participant?.kannactId ?? null,
						name: [participant?.firstName, participant?.lastName]
							.map((part) => part?.trim())
							.filter(Boolean)
							.join(" "),
						// No number hides the call control rather than dialling something wrong.
						phone: phone ? phone : null,
					},
				};
			})
			.sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())
	);
};

/**
 * The coach's local wall-clock time, e.g. "9:00 AM".
 * Deliberately ignores participant.timezone (Maria: "your own clock,
 * same as the whole platform").
 */
export const formatAppointmentTime = (startsAt: Date, locale?: string): string =>
	new Intl.DateTimeFormat(locale, {
		hour: "numeric",
		minute: "2-digit",
	}).format(startsAt);
